import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import Sidebar from '../Components/Sidebar';
import '../Style/AdminSection.css';
import {
  FaUsers, FaShieldAlt, FaFileExport, FaBell,
  FaEye, FaEdit, FaToggleOn, FaToggleOff,
  FaUserFriends, FaPlus, FaTrashAlt, FaSpinner,
  FaIdCard, FaCarSide, FaRoute, FaMoneyBill, FaListUl,
  FaClock,
} from 'react-icons/fa';
import {
  collection, getDocs, doc, updateDoc, getDoc,
  addDoc, deleteDoc, serverTimestamp,
  query, where, orderBy, Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import Papa from 'papaparse';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/* ---------------- Constants ---------------- */
const COLLECTIONS_TO_EXPORT = [
  'users', 'Jobs', 'TestReports', 'FailureReports',
  'Audit', 'Notifications', 'Customers', 'Suppliers',
  'Drivers', 'Vehicles', 'Trips', 'TripCosts'
];

const SIDEBAR_ACCESS_ITEMS = [
  'Dashboard', 'Jobs', 'Create Job', 'Test Report',
  'Failure Report', 'All Reports', 'Settings'
];

/* ---------------- Utilities ---------------- */
const fmtDate = d => {
  if (!d) return '—';
  try {
    if (d.toDate) return d.toDate().toLocaleString();
    if (typeof d === 'string') return new Date(d).toLocaleString();
  } catch {
    return '—';
  }
  return '—';
};

const toYMD = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = `${d.getMonth()+1}`.padStart(2,'0');
  const dd = `${d.getDate()}`.padStart(2,'0');
  return `${y}-${m}-${dd}`;
};

const toHM = (dateObj) => {
  const d = new Date(dateObj);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0,0,0,0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23,59,59,999);
  return x;
};

const debounce = (fn, wait=300) => {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
};

const hoursBetween = (start, end) => {
  if (!start || !end) return 0;
  const ms = Math.max(0, end - start);
  return ms / 36e5; // ms -> hours
};
const fmtHours = (h) => (Math.round(h * 100) / 100).toFixed(2);

/* ---------------- CSV Export ---------------- */
const exportCollections = async () => {
  toast.dismiss();
  toast.info('Exporting collections…', { autoClose: 1500 });
  for (const name of COLLECTIONS_TO_EXPORT) {
    try {
      const snap = await getDocs(collection(db, name));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Export error for', name, err);
      toast.error(`Failed exporting ${name}`);
    }
  }
  toast.success('All exports complete!');
};

/* ---------------- Modal Wrapper ---------------- */
function Modal({ title, children, onClose, wide, size = 'md' }) {
  const sizeClass =
    wide ? 'modal-wide'
    : size === 'xl' ? 'modal-xl'
    : size === 'full' ? 'modal-full'
    : size === 'wide' ? 'modal-wide'
    : '';

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className={`modal-card ${sizeClass}`}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Edit User Form ---------------- */
function EditForm({ user, setUser, onSave, onCancel }) {
  const editableKeys = Object.keys(user)
    .filter(k => !['id', 'email'].includes(k));

  const handleChange = useCallback(e => {
    const { id, value } = e.target;
    setUser(prev => ({ ...prev, [id]: value }));
  }, [setUser]);

  const handleClear = useCallback(() => {
    setUser(prev => {
      const cleared = { ...prev };
      editableKeys.forEach(k => {
        const val = prev[k];
        if (typeof val === 'boolean') cleared[k] = false;
        else if (Array.isArray(val)) cleared[k] = [];
        else if (typeof val === 'number') cleared[k] = 0;
        else cleared[k] = '';
      });
      return cleared;
    });
  }, [editableKeys, setUser]);

  return (
    <div className="form-container">
      <div className="two-col-grid">
        {editableKeys.map(k => (
          <div className="field" key={k}>
            <label htmlFor={k}>{k}</label>
            <input
              id={k}
              value={user[k] ?? ''}
              onChange={handleChange}
            />
          </div>
        ))}
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={() => onSave(user)}>Save</button>
        <button className="btn-clear" onClick={handleClear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Notification Form ---------------- */
function NotificationForm({ users, onSend, onCancel }) {
  const [recipients, setRecipients] = useState(['all']);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState(false);

  const handleSel = useCallback(e => {
    const opts = [...e.target.selectedOptions].map(o => o.value);
    setRecipients(opts.includes('all') ? ['all'] : opts);
  }, []);

  const handleMsg = useCallback(e => {
    const v = e.target.value;
    setMsg(v);
    setError(!v.trim());
  }, []);

  const clear = useCallback(() => {
    setRecipients(['all']);
    setMsg('');
    setError(false);
  }, []);

  const send = () => {
    if (error || !msg.trim()) {
      setError(true);
      return;
    }
    onSend(recipients, msg);
  };

  return (
    <div className="form-container">
      <div className="field">
        <label htmlFor="recipients">Recipients</label>
        <select
          id="recipients"
          multiple
          size={Math.min(users.length + 1, 8)}
          value={recipients}
          onChange={handleSel}
        >
          <option value="all">All Users</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.email}</option>
          ))}
        </select>
        <small className="hint">Hold Ctrl / Cmd to multi-select.</small>
      </div>
      <div className="field">
        <label htmlFor="message">Message <span className="required">*</span></label>
        <textarea
          id="message"
          rows={4}
          value={msg}
          onChange={handleMsg}
          className={error ? 'input-error' : ''}
        />
        {error && <span className="error-text">Message is required</span>}
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={send} disabled={error}>Send</button>
        <button className="btn-clear" onClick={clear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Customer Form ---------------- */
function CustomerForm({ initial, onSave, onCancel }) {
  const [cust, setCust] = useState(initial);
  const [errors, setErrors] = useState({ name: false });

  useEffect(() => setCust(initial), [initial]);

  const handle = useCallback(e => {
    const { name, value } = e.target;
    setCust(p => ({ ...p, [name]: value }));
    if (name === 'name') {
      setErrors(p => ({ ...p, name: !value.trim() }));
    }
  }, []);

  const handleClear = useCallback(() => {
    setCust({
      name: '', vendor: '', cellphone: '', email: '',
      address: '', description: ''
    });
    setErrors({ name: true });
  }, []);

  const validate = useCallback(() => {
    const nxt = { name: !cust.name.trim() };
    setErrors(nxt);
    return !nxt.name;
  }, [cust]);

  const save = () => {
    if (!validate()) {
      toast.error('Please fill required fields (Name).');
      return;
    }
    onSave(cust);
  };

  return (
    <div className="form-container">
      <div className="two-col-grid">
        {[
          ['name','Name', true],
          ['vendor','Vendor', false],
          ['cellphone','Cellphone', false],
          ['email','Email', false],
          ['address','Address', false]
        ].map(([f,label,req]) => (
          <div className="field" key={f}>
            <label htmlFor={f}>{label} {req && <span className="required">*</span>}</label>
            <input
              id={f}
              name={f}
              value={cust[f] ?? ''}
              onChange={handle}
              className={f==='name' && errors.name ? 'input-error' : ''}
            />
            {f==='name' && errors.name && <span className="error-text">Name required</span>}
          </div>
        ))}
      </div>
      <div className="field">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          rows={3}
          value={cust.description}
          onChange={handle}
        />
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={save}>Save</button>
        <button className="btn-clear" onClick={handleClear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Supplier Form ---------------- */
function SupplierForm({ initial, onSave, onCancel }) {
  const [supp, setSupp] = useState(initial);
  const [errors, setErrors] = useState({ name: false, vendorNumber: false });

  useEffect(() => setSupp(initial), [initial]);

  const handle = useCallback(e => {
    const { name, value } = e.target;
    setSupp(p => ({ ...p, [name]: value }));
    if (name === 'name' || name === 'vendorNumber') {
      setErrors(p => ({ ...p, [name]: !value.trim() }));
    }
  }, []);

  const clear = useCallback(() => {
    setSupp({
      name: '', vendorNumber: '', cellphone: '', email: '',
      address: '', description: ''
    });
    setErrors({ name: true, vendorNumber: true });
  }, []);

  const validate = useCallback(() => {
    const nxt = {
      name: !supp.name.trim(),
      vendorNumber: !supp.vendorNumber.trim()
    };
    setErrors(nxt);
    return !nxt.name && !nxt.vendorNumber;
  }, [supp]);

  const save = () => {
    if (!validate()) {
      toast.error('Supplier Name & Vendor Number are required.');
      return;
    }
    onSave(supp);
  };

  return (
    <div className="form-container">
      <div className="two-col-grid">
        {[
          ['name','Name', true],
          ['vendorNumber','Vendor Number', true],
          ['cellphone','Cellphone', false],
          ['email','Email', false],
          ['address','Address', false]
        ].map(([f,label,req]) => (
          <div className="field" key={f}>
            <label htmlFor={f}>{label} {req && <span className="required">*</span>}</label>
            <input
              id={f}
              name={f}
              value={supp[f] ?? ''}
              onChange={handle}
              className={(errors[f] && req) ? 'input-error' : ''}
            />
            {errors[f] && req && (
              <span className="error-text">
                {label} required
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="field">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          rows={3}
          value={supp.description}
          onChange={handle}
        />
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={save}>Save</button>
        <button className="btn-clear" onClick={clear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Driver Form ---------------- */
function DriverForm({ initial, onSave, onCancel }) {
  const [drv, setDrv] = useState(initial);
  const [errors, setErrors] = useState({ name: false, nationalId: false, licenseNumber: false });

  useEffect(() => setDrv(initial), [initial]);

  const handle = e => {
    const { name, value } = e.target;
    setDrv(p => ({ ...p, [name]: value }));
    if (['name','nationalId','licenseNumber'].includes(name)) {
      setErrors(p => ({ ...p, [name]: !value.trim() }));
    }
  };

  const clear = () => {
    setDrv({
      name:'', nationalId:'', licenseNumber:'', phone:'', email:'',
      address:'', notes:''
    });
    setErrors({ name:true, nationalId:true, licenseNumber:true });
  };

  const validate = () => {
    const nxt = {
      name: !drv.name.trim(),
      nationalId: !drv.nationalId.trim(),
      licenseNumber: !drv.licenseNumber.trim()
    };
    setErrors(nxt);
    return !nxt.name && !nxt.nationalId && !nxt.licenseNumber;
  };

  const save = () => {
    if (!validate()) {
      toast.error('Name, ID and License Number are required.');
      return;
    }
    onSave(drv);
  };

  return (
    <div className="form-container">
      <div className="two-col-grid">
        {[
          ['name','Name',true],
          ['nationalId','ID / National ID',true],
          ['licenseNumber','License Number',true],
          ['phone','Phone',false],
          ['email','Email',false],
          ['address','Address',false]
        ].map(([f,label,req]) => (
          <div className="field" key={f}>
            <label htmlFor={f}>{label} {req && <span className="required">*</span>}</label>
            <input
              id={f}
              name={f}
              value={drv[f] ?? ''}
              onChange={handle}
              className={(errors[f] && req) ? 'input-error' : ''}
            />
            {errors[f] && req && <span className="error-text">{label} required</span>}
          </div>
        ))}
      </div>
      <div className="field">
        <label htmlFor="notes">Notes</label>
        <textarea id="notes" name="notes" rows={3} value={drv.notes} onChange={handle}/>
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={save}>Save</button>
        <button className="btn-clear" onClick={clear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Vehicle Form ---------------- */
function VehicleForm({ initial, onSave, onCancel }) {
  const [veh, setVeh] = useState(initial);
  const [errors, setErrors] = useState({ name:false, numberPlate:false });

  useEffect(() => setVeh(initial), [initial]);

  const handle = e => {
    const { name, value } = e.target;
    setVeh(p => ({ ...p, [name]: value }));
    if (['name','numberPlate'].includes(name)) {
      setErrors(p => ({ ...p, [name]: !value.trim() }));
    }
  };

  const clear = () => {
    setVeh({
      name:'', numberPlate:'', make:'', model:'', year:'', color:'', notes:''
    });
    setErrors({ name:true, numberPlate:true });
  };

  const validate = () => {
    const nxt = {
      name: !veh.name.trim(),
      numberPlate: !veh.numberPlate.trim()
    };
    setErrors(nxt);
    return !nxt.name && !nxt.numberPlate;
  };

  const save = () => {
    if (!validate()) {
      toast.error('Vehicle Name and Number Plate are required.');
      return;
    }
    onSave(veh);
  };

  return (
    <div className="form-container">
      <div className="two-col-grid">
        {[
          ['name','Vehicle Name',true],
          ['numberPlate','Number Plate',true],
          ['make','Make',false],
          ['model','Model',false],
          ['year','Year',false],
          ['color','Color',false]
        ].map(([f,label,req]) => (
          <div className="field" key={f}>
            <label htmlFor={f}>{label} {req && <span className="required">*</span>}</label>
            <input
              id={f}
              name={f}
              value={veh[f] ?? ''}
              onChange={handle}
              className={(errors[f] && req) ? 'input-error' : ''}
            />
            {errors[f] && req && <span className="error-text">{label} required</span>}
          </div>
        ))}
      </div>
      <div className="field">
        <label htmlFor="notes">Notes</label>
        <textarea id="notes" name="notes" rows={3} value={veh.notes} onChange={handle}/>
      </div>
      <div className="form-actions">
        <button className="btn-primary" onClick={save}>Save</button>
        <button className="btn-clear" onClick={clear}>Clear</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------------- Trip Form (NO autosave; two sections) ---------------- */
function TripForm({ initial, onSaveTrip, onSaveCost, onCancel, drivers, vehicles, tripCosts }) {
  const [trip, setTrip] = useState(initial);
  const [errors, setErrors] = useState({ name:false, startLocation:false, endLocation:false });

  const [costLabel, setCostLabel] = useState('');
  const [costAmount, setCostAmount] = useState('');

  useEffect(() => setTrip(initial), [initial]);

  const handle = e => {
    const { name, value } = e.target;
    setTrip(p => ({ ...p, [name]: value }));
    if (['name','startLocation','endLocation'].includes(name)) {
      setErrors(p => ({ ...p, [name]: !value.trim() }));
    }
  };

  const pickCost = e => {
    const id = e.target.value;
    const sel = tripCosts.find(c => c.id === id);
    setTrip(p => ({ ...p, costId: id, costAmount: sel ? Number(sel.amount || 0) : 0 }));
  };

  const validateTrip = () => {
    const nxt = {
      name: !trip.name.trim(),
      startLocation: !trip.startLocation.trim(),
      endLocation: !trip.endLocation.trim()
    };
    setErrors(nxt);
    return !nxt.name && !nxt.startLocation && !nxt.endLocation;
  };

  const saveTrip = () => {
    if (!validateTrip()) {
      toast.error('Trip Name, Start and End locations are required.');
      return;
    }
    onSaveTrip({ ...trip, costAmount: Number(trip.costAmount || 0) });
  };

  const saveCost = () => {
    if (!costLabel.trim() || String(costAmount).trim() === '') {
      toast.error('Cost Name and Amount are required.');
      return;
    }
    onSaveCost(costLabel.trim(), Number(costAmount));
    setCostLabel('');
    setCostAmount('');
  };

  return (
    <div className="form-container">
      <div className="section">
        <div className="section-header">
          <h4>Create Trip</h4>
        </div>
        <div className="two-col-grid">
          {[
            ['name','Trip Name',true],
            ['startLocation','Start Location',true],
            ['endLocation','End Location',true]
          ].map(([f,label,req]) => (
            <div className="field" key={f}>
              <label htmlFor={f}>{label} {req && <span className="required">*</span>}</label>
              <input
                id={f}
                name={f}
                value={trip[f] ?? ''}
                onChange={handle}
                className={(errors[f] && req) ? 'input-error' : ''}
              />
              {errors[f] && req && <span className="error-text">{label} required</span>}
            </div>
          ))}
          <div className="field">
            <label htmlFor="date">Date/Time</label>
            <input id="date" name="date" type="datetime-local" value={trip.date || ''} onChange={handle}/>
          </div>
          <div className="field">
            <label htmlFor="driverId">Driver</label>
            <select id="driverId" name="driverId" value={trip.driverId || ''} onChange={handle}>
              <option value="">— Select Driver —</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.licenseNumber})</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="vehicleId">Vehicle</label>
            <select id="vehicleId" name="vehicleId" value={trip.vehicleId || ''} onChange={handle}>
              <option value="">— Select Vehicle —</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} [{v.numberPlate}]</option>)}
            </select>
          </div>

          <div className="field">
            <label htmlFor="costId">Trip Cost (Select Saved)</label>
            <select id="costId" value={trip.costId || ''} onChange={pickCost}>
              <option value="">— Choose a cost —</option>
              {tripCosts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.label} — {Number(c.amount || 0).toFixed(2)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="costAmount">Selected Cost Amount</label>
            <input
              id="costAmount"
              name="costAmount"
              type="number"
              step="0.01"
              value={trip.costAmount ?? 0}
              onChange={e => setTrip(p => ({ ...p, costAmount: Number(e.target.value) }))}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="notes">Notes</label>
          <textarea id="notes" name="notes" rows={3} value={trip.notes || ''} onChange={handle}/>
        </div>

        <div className="form-actions">
          <button className="btn-primary" onClick={saveTrip}>Save Trip</button>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h4>Add Trip Cost</h4>
          <div className="hint">Create reusable cost templates for future trips.</div>
        </div>
        <div className="two-col-grid">
          <div className="field">
            <label htmlFor="costLabel">Cost Name</label>
            <input
              id="costLabel"
              placeholder="e.g. City Center → Mine A"
              value={costLabel}
              onChange={e => setCostLabel(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="costAmountNew">Amount</label>
            <input
              id="costAmountNew"
              type="number"
              step="0.01"
              placeholder="e.g. 350"
              value={costAmount}
              onChange={e => setCostAmount(e.target.value)}
            />
          </div>
        </div>

        {tripCosts.length > 0 && (
          <div className="cost-list">
            <div className="cost-list-header">
              <h4><FaListUl style={{ verticalAlign: 'middle' }} /> Saved Trip Costs</h4>
              <span className="cost-list-hint">Click a cost to apply it to the trip</span>
            </div>

            <div className="cost-grid">
              {tripCosts.map(c => {
                const isActive = trip.costId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`cost-chip ${isActive ? 'is-active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => {
                      setTrip(p => ({ ...p, costId: c.id, costAmount: Number(c.amount || 0) }));
                    }}
                    title="Apply this cost"
                  >
                    <span className="cost-chip__label">{c.label}</span>
                    <span className="cost-chip__amount">
                      {Number(c.amount || 0).toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="form-actions">
        <button className="btn-cancel" onClick={onCancel}>Close</button>
      </div>
    </div>
  );
}

/* ---------------- Main Admin Component ---------------- */
export default function Admin() {
  const [loading, setLoading] = useState(true);

  /* Modal flags */
  const [showUsers, setShowUsers]           = useState(false);
  const [showAccess, setShowAccess]         = useState(false);
  const [showAudit, setShowAudit]           = useState(false);
  const [showNotif, setShowNotif]           = useState(false);
  const [showCustomers, setShowCustomers]   = useState(false);
  const [showAddCust, setShowAddCust]       = useState(false);
  const [editCust, setEditCust]             = useState(null);
  const [showSuppliers, setShowSuppliers]   = useState(false);
  const [showAddSupp, setShowAddSupp]       = useState(false);
  const [editSupp, setEditSupp]             = useState(null);

  // Drivers
  const [showDrivers, setShowDrivers]       = useState(false);
  const [showAddDriver, setShowAddDriver]   = useState(false);
  const [editDriver, setEditDriver]         = useState(null);

  // Vehicles
  const [showVehicles, setShowVehicles]     = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editVehicle, setEditVehicle]       = useState(null);

  // Trips
  const [showTrips, setShowTrips]           = useState(false);
  const [showAddTrip, setShowAddTrip]       = useState(false);
  const [editTrip, setEditTrip]             = useState(null);

  // Employee Times
  const [showTimes, setShowTimes]           = useState(false);
  const [timesRows, setTimesRows]           = useState([]);
  const [timesLoading, setTimesLoading]     = useState(false);
  const [timesSearch, setTimesSearch]       = useState('');
  const [timesStart, setTimesStart]         = useState(() => toYMD(new Date()));
  const [timesEnd, setTimesEnd]             = useState(() => toYMD(new Date()));
  const timesDebounceRef = useRef();

  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);

  /* Data */
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [tripCosts, setTripCosts] = useState([]);

  /* Search text */
  const [custSearch, setCustSearch] = useState('');
  const [suppSearch, setSuppSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [tripSearch, setTripSearch] = useState('');

  /* Debounce setup with cleanup */
  const custDebounceRef = useRef();
  const suppDebounceRef = useRef();
  const drvDebounceRef  = useRef();
  const vehDebounceRef  = useRef();
  const tripDebounceRef = useRef();

  useEffect(() => {
    custDebounceRef.current  = debounce(val => setCustSearch(val), 300);
    suppDebounceRef.current  = debounce(val => setSuppSearch(val), 300);
    drvDebounceRef.current   = debounce(val => setDriverSearch(val), 300);
    vehDebounceRef.current   = debounce(val => setVehicleSearch(val), 300);
    tripDebounceRef.current  = debounce(val => setTripSearch(val), 300);
    timesDebounceRef.current = debounce(val => setTimesSearch(val), 300);
    return () => {
      custDebounceRef.current?.cancel?.();
      suppDebounceRef.current?.cancel?.();
      drvDebounceRef.current?.cancel?.();
      vehDebounceRef.current?.cancel?.();
      tripDebounceRef.current?.cancel?.();
      timesDebounceRef.current?.cancel?.();
    };
  }, []);

  /* Auth init */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async user => {
      if (!user) {
        toast.error('Please login to access admin dashboard.');
        setLoading(false);
        return;
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  /* Lazy fetchers */
  useEffect(() => {
    if (!(showUsers || showAccess || showNotif || viewUser || editUser || showTimes)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        toast.error('Failed fetching users');
      }
    })();
  }, [showUsers, showAccess, showNotif, viewUser, editUser, showTimes]);

  useEffect(() => {
    if (!showAudit) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Audit'));
        setAudit(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching audit logs');
      }
    })();
  }, [showAudit]);

  useEffect(() => {
    if (!(showCustomers || showAddCust || editCust)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Customers'));
        setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching customers');
      }
    })();
  }, [showCustomers, showAddCust, editCust]);

  useEffect(() => {
    if (!(showSuppliers || showAddSupp || editSupp)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Suppliers'));
        setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching suppliers');
      }
    })();
  }, [showSuppliers, showAddSupp, editSupp]);

  useEffect(() => {
    if (!(showDrivers || showAddDriver || editDriver)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Drivers'));
        setDrivers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching drivers');
      }
    })();
  }, [showDrivers, showAddDriver, editDriver]);

  useEffect(() => {
    if (!(showVehicles || showAddVehicle || editVehicle)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Vehicles'));
        setVehicles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching vehicles');
      }
    })();
  }, [showVehicles, showAddVehicle, editVehicle]);

  useEffect(() => {
    if (!(showTrips || showAddTrip || editTrip)) return;
    (async () => {
      try {
        const [tSnap, dSnap, vSnap, cSnap] = await Promise.all([
          getDocs(collection(db, 'Trips')),
          getDocs(collection(db, 'Drivers')),
          getDocs(collection(db, 'Vehicles')),
          getDocs(collection(db, 'TripCosts'))
        ]);
        setTrips(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setDrivers(dSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setVehicles(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTripCosts(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        toast.error('Failed fetching trip data');
      }
    })();
  }, [showTrips, showAddTrip, editTrip]);

  /* ---------- Employee Times: fetch attendance in range & build sessions ---------- */
  const loadTimes = useCallback(async () => {
    try {
      setTimesLoading(true);
      const start = startOfDay(new Date(timesStart));
      const end = endOfDay(new Date(timesEnd));
      const attCol = collection(db, 'attendance');

      const q = query(
        attCol,
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end)),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Map users for fallback info
      const userById = new Map(users.map(u => [u.id, u]));

      // Group events per user per day
      const grouped = new Map(); // key -> { userId, name, email, day, events: [{type, ts}] }
      for (const rec of items) {
        const ts = rec.date?.toDate ? rec.date.toDate() : new Date(rec.date);
        const day = toYMD(ts);
        const uid = rec.userId || 'unknown';
        const key = `${uid}|${day}`;
        if (!grouped.has(key)) {
          const u = userById.get(uid) || {};
          grouped.set(key, {
            key,
            userId: uid,
            day,
            name: `${rec.firstName || u.firstName || ''} ${rec.lastName || u.lastName || ''}`.trim() || '—',
            email: rec.email || u.email || '—',
            events: [],
          });
        }
        grouped.get(key).events.push({ type: rec.type, ts });
      }

      // For each group, pair sessions IN->OUT (multiple per day)
      const rows = [];
      for (const g of grouped.values()) {
        // sort by time
        g.events.sort((a, b) => a.ts - b.ts);

        const sessions = [];
        let openIn = null;
        for (const ev of g.events) {
          if (ev.type === 'in') {
            // start/replace current open session
            openIn = ev.ts;
          } else if (ev.type === 'out') {
            if (openIn) {
              const hrs = hoursBetween(openIn, ev.ts);
              sessions.push({ in: openIn, out: ev.ts, hours: hrs });
              openIn = null;
            } else {
              // stray "out" without a prior "in" -> ignore
            }
          }
        }
        // If the last IN has no OUT, keep an open session (hours = 0 until closed)
        if (openIn) {
          sessions.push({ in: openIn, out: null, hours: 0 });
        }

        const totalHours = sessions.reduce((acc, s) => acc + (s.hours || 0), 0);
        const firstIn = sessions.length ? sessions[0].in : null;
        const lastOut = [...sessions].reverse().find(s => s.out)?.out || null;

        rows.push({
          key: g.key,
          userId: g.userId,
          day: g.day,
          name: g.name,
          email: g.email,
          sessions,
          firstIn,
          lastOut,
          hours: totalHours,
        });
      }

      // Sort newest day first, then by name
      rows.sort((a, b) => {
        if (a.day === b.day) return a.name.localeCompare(b.name);
        return b.day.localeCompare(a.day);
      });

      setTimesRows(rows);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load employee times');
    } finally {
      setTimesLoading(false);
    }
  }, [timesStart, timesEnd, users]);

  useEffect(() => {
    if (!showTimes) return;
    loadTimes();
  }, [showTimes, timesStart, timesEnd, loadTimes]);

  /* Filtered results for Times */
  const filteredTimes = useMemo(() => {
    const q = timesSearch.trim().toLowerCase();
    if (!q) return timesRows;
    return timesRows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q)
    );
  }, [timesRows, timesSearch]);

  // Aggregate stats (over filtered rows)
  const timeStats = useMemo(() => {
    if (!filteredTimes.length) {
      return {
        label: 'All Users',
        totalRows: 0,
        workedRows: 0,
        totalHours: 0,
        avgHours: 0,
        earliestIn: null,
        latestOut: null,
        perUserTotals: [],
      };
    }
    const totalRows = filteredTimes.length;
    const workedRows = filteredTimes.filter(r => r.hours > 0).length;
    const totalHours = filteredTimes.reduce((acc, r) => acc + (r.hours || 0), 0);
    const avgHours = workedRows ? totalHours / workedRows : 0;

    const earliestIn = filteredTimes
      .map(r => r.firstIn)
      .filter(Boolean)
      .reduce((min, d) => (min && d ? (d < min ? d : min) : d), null);

    const latestOut = filteredTimes
      .map(r => r.lastOut)
      .filter(Boolean)
      .reduce((max, d) => (max && d ? (d > max ? d : max) : d), null);

    // Totals per user across the (filtered) date range
    const perUser = new Map();
    for (const r of filteredTimes) {
      const cur = perUser.get(r.userId) || { name: r.name, hours: 0 };
      cur.hours += r.hours || 0;
      perUser.set(r.userId, cur);
    }
    const perUserTotals = Array.from(perUser.values()).sort((a,b)=>b.hours-a.hours);

    const uniqUsers = Array.from(perUser.keys());
    const label = uniqUsers.length === 1 ? perUser.get(uniqUsers[0]).name : 'All Users';

    return { label, totalRows, workedRows, totalHours, avgHours, earliestIn, latestOut, perUserTotals };
  }, [filteredTimes]);

  /* ---------- Filtered results for other resources ---------- */

  const filteredCustomers = useMemo(() => {
    const q = custSearch.toLowerCase();
    return customers.filter(c =>
      JSON.stringify(c).toLowerCase().includes(q)
    );
  }, [customers, custSearch]);

  const filteredSuppliers = useMemo(() => {
    const q = suppSearch.toLowerCase();
    return suppliers.filter(s =>
      JSON.stringify(s).toLowerCase().includes(q)
    );
  }, [suppliers, suppSearch]);

  const filteredDrivers = useMemo(() => {
    const q = driverSearch.toLowerCase();
    return drivers.filter(d =>
      JSON.stringify(d).toLowerCase().includes(q)
    );
  }, [drivers, driverSearch]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.toLowerCase();
    return vehicles.filter(v =>
      JSON.stringify(v).toLowerCase().includes(q)
    );
  }, [vehicles, vehicleSearch]);

  const filteredTrips = useMemo(() => {
    const q = tripSearch.toLowerCase();
    return trips.filter(t =>
      JSON.stringify(t).toLowerCase().includes(q)
    );
  }, [trips, tripSearch]);

  /* ---------- User Functions ---------- */
  const toggleDisable = useCallback(async u => {
    try {
      await updateDoc(doc(db, 'users', u.id), { disabled: !u.disabled });
      setUsers(s => s.map(x => x.id === u.id ? { ...x, disabled: !x.disabled } : x));
      toast.success(`${u.email} ${u.disabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Toggle failed');
    }
  }, []);

  const toggleAccess = useCallback(async (uid, item) => {
    try {
      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const acl = data.access || {};
      const next = { ...acl, [item]: !(acl[item] ?? true) };
      await updateDoc(ref, { access: next });
      setUsers(s => s.map(x => x.id === uid ? { ...x, access: next } : x));
      toast.success('Access updated');
    } catch {
      toast.error('Access update failed');
    }
  }, []);

  const saveUser = useCallback(async u => {
    try {
      const { id, ...rest } = u;
      await updateDoc(doc(db, 'users', id), rest);
      setUsers(s => s.map(x => x.id === id ? u : x));
      toast.success('User saved');
      setEditUser(null);
    } catch {
      toast.error('Save failed');
    }
  }, []);

  /* ---------- Customer CRUD ---------- */
  const addCustomer = useCallback(async cust => {
    if (!cust.name?.trim()) {
      toast.error('Customer name required');
      return;
    }
    try {
      await addDoc(collection(db, 'Customers'), {
        name: cust.name.trim(),
        vendor: cust.vendor?.trim() || '—',
        cellphone: cust.cellphone?.trim() || '—',
        email: cust.email?.trim() || '—',
        address: cust.address?.trim() || '—',
        description: cust.description?.trim() || '—',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Customer added');
      setShowAddCust(false);
    } catch {
      toast.error('Add failed');
    }
  }, []);

  const updateCustomer = useCallback(async cust => {
    if (!cust.name?.trim()) {
      toast.error('Customer name required');
      return;
    }
    try {
      const { id, ...rest } = cust;
      await updateDoc(doc(db, 'Customers', id), {
        name: rest.name.trim(),
        vendor: rest.vendor?.trim() || '—',
        cellphone: rest.cellphone?.trim() || '—',
        email: rest.email?.trim() || '—',
        address: rest.address?.trim() || '—',
        description: rest.description?.trim() || '—'
      });
      setCustomers(s => s.map(c => c.id === id ? cust : c));
      toast.success('Customer updated');
      setEditCust(null);
    } catch {
      toast.error('Update failed');
    }
  }, []);

  const deleteCustomer = useCallback(async id => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await deleteDoc(doc(db, 'Customers', id));
      setCustomers(s => s.filter(c => c.id !== id));
      toast.success('Customer deleted');
    } catch {
      toast.error('Delete failed');
    }
  }, []);

  /* ---------- Supplier CRUD ---------- */
  const addSupplier = useCallback(async supp => {
    if (!supp.name?.trim() || !supp.vendorNumber?.trim()) {
      toast.error('Name & Vendor Number required');
      return;
    }
    try {
      await addDoc(collection(db, 'Suppliers'), {
        name: supp.name.trim(),
        vendorNumber: supp.vendorNumber.trim(),
        cellphone: supp.cellphone?.trim() || '—',
        email: supp.email?.trim() || '—',
        address: supp.address?.trim() || '—',
        description: supp.description?.trim() || '—',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Supplier added');
      setShowAddSupp(false);
    } catch {
      toast.error('Add failed');
    }
  }, []);

  const updateSupplier = useCallback(async supp => {
    if (!supp.name?.trim() || !supp.vendorNumber?.trim()) {
      toast.error('Name & Vendor Number required');
      return;
    }
    try {
      const { id, ...rest } = supp;
      await updateDoc(doc(db, 'Suppliers', id), {
        name: rest.name.trim(),
        vendorNumber: rest.vendorNumber.trim(),
        cellphone: rest.cellphone?.trim() || '—',
        email: rest.email?.trim() || '—',
        address: rest.address?.trim() || '—',
        description: rest.description?.trim() || '—'
      });
      setSuppliers(s => s.map(c => c.id === id ? supp : c));
      toast.success('Supplier updated');
      setEditSupp(null);
    } catch {
      toast.error('Update failed');
    }
  }, []);

  const deleteSupplier = useCallback(async id => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await deleteDoc(doc(db, 'Suppliers', id));
      setSuppliers(s => s.filter(su => su.id !== id));
      toast.success('Supplier deleted');
    } catch {
      toast.error('Delete failed');
    }
  }, []);

  /* ---------- Drivers CRUD ---------- */
  const addDriver = useCallback(async drv => {
    try {
      await addDoc(collection(db, 'Drivers'), {
        ...drv,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Driver added');
      setShowAddDriver(false);
    } catch {
      toast.error('Add driver failed');
    }
  }, []);

  const updateDriver = useCallback(async drv => {
    try {
      const { id, ...rest } = drv;
      await updateDoc(doc(db, 'Drivers', id), rest);
      setDrivers(s => s.map(d => d.id === id ? drv : d));
      toast.success('Driver updated');
      setEditDriver(null);
    } catch {
      toast.error('Update driver failed');
    }
  }, []);

  const deleteDriver = useCallback(async id => {
    if (!window.confirm('Delete this driver?')) return;
    try {
      await deleteDoc(doc(db, 'Drivers', id));
      setDrivers(s => s.filter(d => d.id !== id));
      toast.success('Driver deleted');
    } catch {
      toast.error('Delete driver failed');
    }
  }, []);

  /* ---------- Vehicles CRUD ---------- */
  const addVehicle = useCallback(async veh => {
    try {
      await addDoc(collection(db, 'Vehicles'), {
        ...veh,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Vehicle added');
      setShowAddVehicle(false);
    } catch {
      toast.error('Add vehicle failed');
    }
  }, []);

  const updateVehicle = useCallback(async veh => {
    try {
      const { id, ...rest } = veh;
      await updateDoc(doc(db, 'Vehicles', id), rest);
      setVehicles(s => s.map(v => v.id === id ? veh : v));
      toast.success('Vehicle updated');
      setEditVehicle(null);
    } catch {
      toast.error('Update vehicle failed');
    }
  }, []);

  const deleteVehicle = useCallback(async id => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await deleteDoc(doc(db, 'Vehicles', id));
      setVehicles(s => s.filter(v => v.id !== id));
      toast.success('Vehicle deleted');
    } catch {
      toast.error('Delete vehicle failed');
    }
  }, []);

  /* ---------- Trip Costs ---------- */
  const createTripCost = useCallback(async (label, amount) => {
    try {
      await addDoc(collection(db, 'TripCosts'), {
        label, amount: Number(amount), createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Trip cost saved');
      const cSnap = await getDocs(collection(db, 'TripCosts'));
      setTripCosts(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      toast.error('Saving trip cost failed');
    }
  }, []);

  /* ---------- Trips CRUD ---------- */
  const addTrip = useCallback(async trip => {
    try {
      await addDoc(collection(db, 'Trips'), {
        ...trip,
        costAmount: Number(trip.costAmount || 0),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || 'admin'
      });
      toast.success('Trip added');
      setShowAddTrip(false);
    } catch {
      toast.error('Add trip failed');
    }
  }, []);

  const updateTrip = useCallback(async trip => {
    try {
      const { id, ...rest } = trip;
      await updateDoc(doc(db, 'Trips', id), {
        ...rest, costAmount: Number(rest.costAmount || 0)
      });
      setTrips(s => s.map(t => t.id === id ? trip : t));
      toast.success('Trip updated');
      setEditTrip(null);
    } catch {
      toast.error('Update trip failed');
    }
  }, []);

  const deleteTrip = useCallback(async id => {
    if (!window.confirm('Delete this trip?')) return;
    try {
      await deleteDoc(doc(db, 'Trips', id));
      setTrips(s => s.filter(t => t.id !== id));
      toast.success('Trip deleted');
    } catch {
      toast.error('Delete trip failed');
    }
  }, []);

  /* ---------- Notifications ---------- */
  const sendNotif = useCallback(async (recipients, msg) => {
    if (!msg.trim()) {
      toast.error('Message required');
      return;
    }
    try {
      const col = collection(db, 'Notifications');
      const date = new Date().toISOString();
      const from = auth.currentUser?.uid || 'admin';
      const target = recipients.includes('all') ? users.map(u => u.id) : recipients;
      await Promise.all(
        target.map(uid => addDoc(col, { to: uid, message: msg, createdAt: date, from }))
      );
      toast.success('Notification sent');
      setShowNotif(false);
    } catch {
      toast.error('Send failed');
    }
  }, [users]);

  /* ---------- Dashboard Cards ---------- */
  const cards = [
    { id: 1, title: 'Users & Roles', icon: <FaUsers />, action: () => setShowUsers(true) },
    { id: 2, title: 'Access Control', icon: <FaShieldAlt />, action: () => setShowAccess(true) },
    { id: 3, title: 'Audit Log', icon: <FaShieldAlt />, action: () => setShowAudit(true) },
    { id: 4, title: 'Data Export', icon: <FaFileExport />, action: exportCollections },
    { id: 5, title: 'Notification', icon: <FaBell />, action: () => setShowNotif(true) },
    { id: 6, title: 'Customers', icon: <FaUserFriends />, action: () => setShowCustomers(true) },
    { id: 7, title: 'Add Customer', icon: <FaPlus />, action: () => setShowAddCust(true) },
    { id: 8, title: 'Suppliers', icon: <FaUserFriends />, action: () => setShowSuppliers(true) },
    { id: 9, title: 'Add Supplier', icon: <FaPlus />, action: () => setShowAddSupp(true) },

    // Drivers, Vehicles, Trips
    { id: 10, title: 'Drivers', icon: <FaIdCard />, action: () => setShowDrivers(true) },
    { id: 11, title: 'Add Driver', icon: <FaPlus />, action: () => setShowAddDriver(true) },
    { id: 12, title: 'Vehicles', icon: <FaCarSide />, action: () => setShowVehicles(true) },
    { id: 13, title: 'Add Vehicle', icon: <FaPlus />, action: () => setShowAddVehicle(true) },
    { id: 14, title: 'Trips', icon: <FaRoute />, action: () => setShowTrips(true) },
    { id: 15, title: 'Add Trip', icon: <FaMoneyBill />, action: () => setShowAddTrip(true) },

    // Employee Times
    { id: 16, title: 'Employee Times', icon: <FaClock />, action: () => setShowTimes(true) },
  ];

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div className="admin-page">
        <Sidebar />
        <div className="admin-loading">
          <FaSpinner className="spin" size={44} />
          <p>Loading admin dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000}/>
      <Sidebar />
      <main className="admin-main">
        <div className="header-row">
          <h1>Admin Dashboard</h1>
        </div>
        <div className="cards-container">
          {cards.map(c => (
            <button
              key={c.id}
              className="admin-card"
              onClick={c.action}
            >
              <div className="card-header">
                <div className="card-icon">{c.icon}</div>
                <div className="card-title">{c.title}</div>
              </div>
              <div className="card-number">#{c.id}</div>
            </button>
          ))}
        </div>
      </main>

      {/* Users List */}
      {showUsers && (
        <Modal title="Users & Roles" onClose={() => setShowUsers(false)} size="xl">
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.role ?? '—'}</td>
                    <td>{u.disabled ? 'Disabled' : 'Active'}</td>
                    <td className="tbl-actions center">
                      <FaEye onClick={() => setViewUser(u)} title="View"/>
                      <FaEdit onClick={() => setEditUser({ ...u })} title="Edit"/>
                      {u.disabled
                        ? <FaToggleOff onClick={() => toggleDisable(u)} title="Enable"/>
                        : <FaToggleOn onClick={() => toggleDisable(u)} title="Disable"/>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* View User */}
      {viewUser && (
        <Modal title="User Details" onClose={() => setViewUser(null)} size="xl">
          <div className="scroll-y taller">
            <table className="kv-table">
              <tbody>
                {Object.entries(viewUser).map(([k,v]) => (
                  <tr key={k}>
                    <th>{k}</th>
                    <td className="wrap">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Edit User */}
      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)}>
          <EditForm
            user={editUser}
            setUser={setEditUser}
            onSave={saveUser}
            onCancel={() => setEditUser(null)}
          />
        </Modal>
      )}

      {/* Access Control */}
      {showAccess && (
        <Modal title="Access Control" onClose={() => setShowAccess(false)} size="xl">
          <div className="scroll-x">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>User</th>
                  {SIDEBAR_ACCESS_ITEMS.map(i => <th key={i}>{i}</th>)}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    {SIDEBAR_ACCESS_ITEMS.map(item => {
                      const allowed = u.access?.[item] ?? true;
                      return (
                        <td key={item} className="center">
                          <input
                            type="checkbox"
                            checked={allowed}
                            onChange={() => toggleAccess(u.id, item)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Audit Log */}
      {showAudit && (
        <Modal title="Audit Log" onClose={() => setShowAudit(false)} size="xl">
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>UID</th>
                  <th>Event</th>
                  <th>Date</th>
                  <th>TZ Offset</th>
                  <th>User Agent</th>
                </tr>
              </thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id}>
                    <td>{a.email}</td>
                    <td>{a.uid}</td>
                    <td>{a.event}</td>
                    <td>{fmtDate(a.createdAt)}</td>
                    <td>{a.tzOffset}</td>
                    <td className="ellipsis">{a.userAgent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Notifications */}
      {showNotif && (
        <Modal title="Send Notification" onClose={() => setShowNotif(false)}>
          <NotificationForm
            users={users}
            onSend={sendNotif}
            onCancel={() => setShowNotif(false)}
          />
        </Modal>
      )}

      {/* Customers */}
      {showCustomers && (
        <Modal title="Customers" onClose={() => setShowCustomers(false)} size="xl">
          <div className="field compact">
            <label htmlFor="custSearch">Search Customers</label>
            <input
              id="custSearch"
              placeholder="Type to search…"
              onChange={e => custDebounceRef.current(e.target.value)}
            />
          </div>
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Vendor</th>
                  <th>Cell</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Description</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.vendor || '—'}</td>
                    <td>{c.cellphone}</td>
                    <td>{c.email}</td>
                    <td className="wrap">{c.address}</td>
                    <td className="wrap">{c.description}</td>
                    <td className="tbl-actions center">
                      <FaEdit onClick={() => setEditCust({ ...c })} title="Edit"/>
                      <FaTrashAlt onClick={() => deleteCustomer(c.id)} title="Delete"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Suppliers */}
      {showSuppliers && (
        <Modal title="Suppliers" onClose={() => setShowSuppliers(false)} size="xl">
          <div className="field compact">
            <label htmlFor="suppSearch">Search Suppliers</label>
            <input
              id="suppSearch"
              placeholder="Type to search…"
              onChange={e => suppDebounceRef.current(e.target.value)}
            />
          </div>
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Vendor No.</th>
                  <th>Cell</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Description</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map(s => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.vendorNumber}</td>
                    <td>{s.cellphone}</td>
                    <td>{s.email}</td>
                    <td className="wrap">{s.address}</td>
                    <td className="wrap">{s.description}</td>
                    <td className="tbl-actions center">
                      <FaEdit onClick={() => setEditSupp({ ...s })} title="Edit"/>
                      <FaTrashAlt onClick={() => deleteSupplier(s.id)} title="Delete"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Add / Edit Customer */}
      {(showAddCust || editCust) && (
        <Modal
          title={editCust ? 'Edit Customer' : 'Add Customer'}
          onClose={() => { setShowAddCust(false); setEditCust(null); }}
        >
          <CustomerForm
            initial={editCust || {
              name: '', vendor:'', cellphone: '', email: '', address: '', description: ''
            }}
            onSave={editCust ? updateCustomer : addCustomer}
            onCancel={() => { setShowAddCust(false); setEditCust(null); }}
          />
        </Modal>
      )}

      {/* Add / Edit Supplier */}
      {(showAddSupp || editSupp) && (
        <Modal
          title={editSupp ? 'Edit Supplier' : 'Add Supplier'}
          onClose={() => { setShowAddSupp(false); setEditSupp(null); }}
        >
          <SupplierForm
            initial={editSupp || {
              name: '', vendorNumber: '', cellphone: '', email: '', address: '', description: ''
            }}
            onSave={editSupp ? updateSupplier : addSupplier}
            onCancel={() => { setShowAddSupp(false); setEditSupp(null); }}
          />
        </Modal>
      )}

      {/* Drivers (View) */}
      {showDrivers && (
        <Modal title="Drivers" onClose={() => setShowDrivers(false)} size="xl">
          <div className="field compact">
            <label htmlFor="drvSearch">Search Drivers</label>
            <input
              id="drvSearch"
              placeholder="Type to search…"
              onChange={e => drvDebounceRef.current(e.target.value)}
            />
          </div>
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>ID</th>
                  <th>License #</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Notes</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map(d => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>{d.nationalId}</td>
                    <td>{d.licenseNumber}</td>
                    <td>{d.phone || '—'}</td>
                    <td>{d.email || '—'}</td>
                    <td className="wrap">{d.address || '—'}</td>
                    <td className="wrap">{d.notes || '—'}</td>
                    <td className="tbl-actions center">
                      <FaEdit onClick={() => setEditDriver({ ...d })} title="Edit"/>
                      <FaTrashAlt onClick={() => deleteDriver(d.id)} title="Delete"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Add / Edit Driver */}
      {(showAddDriver || editDriver) && (
        <Modal
          title={editDriver ? 'Edit Driver' : 'Add Driver'}
          onClose={() => { setShowAddDriver(false); setEditDriver(null); }}
        >
          <DriverForm
            initial={editDriver || {
              name:'', nationalId:'', licenseNumber:'', phone:'', email:'', address:'', notes:''
            }}
            onSave={editDriver ? updateDriver : addDriver}
            onCancel={() => { setShowAddDriver(false); setEditDriver(null); }}
          />
        </Modal>
      )}

      {/* Vehicles (View) */}
      {showVehicles && (
        <Modal title="Vehicles" onClose={() => setShowVehicles(false)} size="xl">
          <div className="field compact">
            <label htmlFor="vehSearch">Search Vehicles</label>
            <input
              id="vehSearch"
              placeholder="Type to search…"
              onChange={e => vehDebounceRef.current(e.target.value)}
            />
          </div>
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Number Plate</th>
                  <th>Make</th>
                  <th>Model</th>
                  <th>Year</th>
                  <th>Color</th>
                  <th>Notes</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.map(v => (
                  <tr key={v.id}>
                    <td>{v.name}</td>
                    <td>{v.numberPlate}</td>
                    <td>{v.make || '—'}</td>
                    <td>{v.model || '—'}</td>
                    <td>{v.year || '—'}</td>
                    <td>{v.color || '—'}</td>
                    <td className="wrap">{v.notes || '—'}</td>
                    <td className="tbl-actions center">
                      <FaEdit onClick={() => setEditVehicle({ ...v })} title="Edit"/>
                      <FaTrashAlt onClick={() => deleteVehicle(v.id)} title="Delete"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Add / Edit Vehicle */}
      {(showAddVehicle || editVehicle) && (
        <Modal
          title={editVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
          onClose={() => { setShowAddVehicle(false); setEditVehicle(null); }}
        >
          <VehicleForm
            initial={editVehicle || {
              name:'', numberPlate:'', make:'', model:'', year:'', color:'', notes:''
            }}
            onSave={editVehicle ? updateVehicle : addVehicle}
            onCancel={() => { setShowAddVehicle(false); setEditVehicle(null); }}
          />
        </Modal>
      )}

      {/* Trips (View) */}
      {showTrips && (
        <Modal title="Trips" onClose={() => setShowTrips(false)} size="xl">
          <div className="field compact">
            <label htmlFor="tripSearch">Search Trips</label>
            <input
              id="tripSearch"
              placeholder="Type to search…"
              onChange={e => tripDebounceRef.current(e.target.value)}
            />
          </div>
          <div className="scroll-y taller">
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Date</th>
                  <th>Driver</th>
                  <th>Vehicle</th>
                  <th>Cost</th>
                  <th>Notes</th>
                  <th className="center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map(t => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.startLocation}</td>
                    <td>{t.endLocation}</td>
                    <td>{t.date ? new Date(t.date).toLocaleString() : '—'}</td>
                    <td>{drivers.find(d => d.id === t.driverId)?.name || '—'}</td>
                    <td>{vehicles.find(v => v.id === t.vehicleId)?.name || '—'}</td>
                    <td>{Number(t.costAmount || 0).toFixed(2)}</td>
                    <td className="wrap">{t.notes || '—'}</td>
                    <td className="tbl-actions center">
                      <FaEdit onClick={() => setEditTrip({ ...t })} title="Edit"/>
                      <FaTrashAlt onClick={() => deleteTrip(t.id)} title="Delete"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* Add / Edit Trip */}
      {(showAddTrip || editTrip) && (
        <Modal
          title={editTrip ? 'Edit Trip' : 'Add Trip'}
          onClose={() => { setShowAddTrip(false); setEditTrip(null); }}
          size="xl"
        >
          <TripForm
            initial={editTrip || {
              name:'', startLocation:'', endLocation:'', date:'',
              driverId:'', vehicleId:'', costId:'', costAmount:0, notes:''
            }}
            onSaveTrip={editTrip ? updateTrip : addTrip}
            onSaveCost={createTripCost}
            onCancel={() => { setShowAddTrip(false); setEditTrip(null); }}
            drivers={drivers}
            vehicles={vehicles}
            tripCosts={tripCosts}
          />
        </Modal>
      )}

      {/* Employee Times Modal */}
      {showTimes && (
        <Modal title="Employee Times" onClose={() => setShowTimes(false)} size="xl">
          <div className="filters-row" style={{ display:'flex', flexWrap:'wrap', gap:'0.75rem', alignItems:'flex-end' }}>
            <div className="field compact">
              <label htmlFor="timesSearch">Search (name/email)</label>
              <input
                id="timesSearch"
                placeholder="Type to search…"
                onChange={(e) => timesDebounceRef.current(e.target.value)}
              />
            </div>
            <div className="field compact">
              <label htmlFor="timesStart">Start date</label>
              <input
                id="timesStart"
                type="date"
                value={timesStart}
                onChange={(e) => setTimesStart(e.target.value)}
              />
            </div>
            <div className="field compact">
              <label htmlFor="timesEnd">End date</label>
              <input
                id="timesEnd"
                type="date"
                value={timesEnd}
                onChange={(e) => setTimesEnd(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => { const today = toYMD(new Date()); setTimesStart(today); setTimesEnd(today); }}
              style={{ height: 38 }}
            >
              Today
            </button>
            <button
              type="button"
              className="btn-clear"
              onClick={loadTimes}
              style={{ height: 38 }}
              title="Refresh"
            >
              Refresh
            </button>
          </div>

          <div className="scroll-y taller" style={{ position:'relative' }}>
            {timesLoading && (
              <div className="loading-overlay">
                <FaSpinner className="spin" size={28} />
                <span style={{ marginLeft: 8 }}>Loading…</span>
              </div>
            )}
            <table className="sticky-head">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Sessions (IN → OUT)</th>
                  <th>Total Hours</th>
                </tr>
              </thead>
              <tbody>
                {filteredTimes.length === 0 && !timesLoading && (
                  <tr>
                    <td colSpan={5} className="center">No attendance found for the selected range.</td>
                  </tr>
                )}
                {filteredTimes.map(r => (
                  <tr key={r.key}>
                    <td>{r.day}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                 <td className="wrap">
  {r.sessions.length
    ? r.sessions.map((s, idx) => (
        <React.Fragment key={idx}>
          <span className="session-chip">
            {toHM(s.in)} — {s.out ? toHM(s.out) : '—'}
          </span>
          {idx < r.sessions.length - 1 && (
            <span className="session-divider" aria-hidden="true">│</span>
          )}
        </React.Fragment>
      ))
    : '—'}
</td>

                    <td>{fmtHours(r.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stats footer */}
          <div
            className="stats-footer"
            style={{
              marginTop: '0.75rem',
              padding: '0.75rem 1rem',
              background: '#f6f8fa',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
              gap: '0.75rem'
            }}
          >
            <div><strong>Scope:</strong> {timeStats.label}</div>
            <div><strong>Days Listed:</strong> {timeStats.totalRows}</div>
            <div><strong>Days Worked:</strong> {timeStats.workedRows}</div>
            <div><strong>Total Hours:</strong> {fmtHours(timeStats.totalHours)}</div>
            <div><strong>Avg Hours/Worked Day:</strong> {fmtHours(timeStats.avgHours)}</div>
            <div><strong>Earliest In:</strong> {timeStats.earliestIn ? `${toYMD(timeStats.earliestIn)} ${toHM(timeStats.earliestIn)}` : '—'}</div>
            <div><strong>Latest Out:</strong> {timeStats.latestOut ? `${toYMD(timeStats.latestOut)} ${toHM(timeStats.latestOut)}` : '—'}</div>
          </div>

          {/* Optional: totals by user across the shown range */}
          {timeStats.perUserTotals.length > 1 && (
            <div style={{ marginTop:'0.5rem' }}>
              <h4 style={{ margin:'8px 0' }}>Totals by User (in range)</h4>
              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {timeStats.perUserTotals.map((u,i)=>(
                    <tr key={i}>
                      <td>{u.name}</td>
                      <td>{fmtHours(u.hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
